import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { mostrarToast } from '@/lib/formularioGuiado';
import FundoEvento from '@/components/feature/FundoEvento';
import FormularioNovaSenha from './components/FormularioNovaSenha';
import type { EstadoLink, RespostaVerLink, SessaoNovaSenha } from './types';

const TEXTO_FIM = 'Peça um novo link para quem te ajudou.';

// Códigos (sem espaço) não são texto para a pessoa ler.
function fraseLegivel(erro: string): string {
  if (!erro || !/\s/.test(erro)) return 'O link venceu ou já foi usado.';
  return erro;
}

// Lê a mensagem da função quando o link não vale (erro HTTP).
async function erroDoInvoke(error: unknown, padrao: string): Promise<string> {
  const ctx = (error as { context?: Response }).context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const corpo = (await ctx.json()) as Record<string, unknown> | null;
      if (corpo && typeof corpo.erro === 'string' && corpo.erro) return corpo.erro;
      if (corpo && typeof corpo.error === 'string' && corpo.error) return corpo.error;
    } catch {
      // sem corpo legível: usa a mensagem padrão
    }
  }
  return padrao;
}

// ---------------------------------------------------------------------------
// Página pública do link de nova senha (/nova-senha/:token).
// Sem login, sem portão do app, sem faixa de instalar: quem recebeu o link
// confirma CPF e data de nascimento, cria a senha e já entra.
// ---------------------------------------------------------------------------
export default function NovaSenhaPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();

  const [estado, setEstado] = useState<EstadoLink>('conferindo');
  const [nome, setNome] = useState('');
  const [emailMascarado, setEmailMascarado] = useState('');
  const [pedeCpf, setPedeCpf] = useState(false);
  const [pedeNascimento, setPedeNascimento] = useState(false);
  const [erro, setErro] = useState('');
  const [emailCompleto, setEmailCompleto] = useState('');
  const [sessaoPendente, setSessaoPendente] = useState<SessaoNovaSenha | null>(null);
  const [copiado, setCopiado] = useState(false);
  const [entrando, setEntrando] = useState(false);

  useEffect(() => {
    if (!token) {
      setErro('Este link não vale mais.');
      setEstado('invalido');
      return;
    }

    let ativo = true;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('nova-senha', {
          body: { acao: 'ver', token },
        });

        if (error) {
          const msg = await erroDoInvoke(error, 'Este link não vale mais.');
          if (ativo) {
            setErro(msg);
            setEstado('invalido');
          }
          return;
        }

        const resposta = (data ?? {}) as RespostaVerLink;
        if (!ativo) return;
        if (!resposta.ok) {
          setErro(resposta.erro || 'Este link não vale mais.');
          setEstado('invalido');
          return;
        }
        setNome(typeof resposta.nome === 'string' ? resposta.nome : '');
        setEmailMascarado(
          typeof resposta.email_mascarado === 'string' ? resposta.email_mascarado : '',
        );
        setPedeCpf(resposta.pede_cpf === true);
        setPedeNascimento(resposta.pede_nascimento === true);
        setEstado('ok');
      } catch {
        if (ativo) {
          setErro('Este link não vale mais.');
          setEstado('invalido');
        }
      }
    })();

    return () => {
      ativo = false;
    };
  }, [token]);

  // A senha já foi trocada: mostra a tela de sucesso com o e-mail antes de entrar.
  function aoTrocar(sessao: SessaoNovaSenha | null, email: string) {
    setSessaoPendente(sessao);
    setEmailCompleto(email || '');
    setCopiado(false);
    setEstado('sucesso');
  }

  async function continuar() {
    if (sessaoPendente) {
      setEntrando(true);
      const { error } = await supabase.auth.setSession({
        access_token: sessaoPendente.access_token,
        refresh_token: sessaoPendente.refresh_token,
      });
      setEntrando(false);
      if (!error) {
        mostrarToast('Senha trocada! Você já está dentro.', 5000, 'sucesso');
        navigate('/', { replace: true });
        return;
      }
    }
    mostrarToast('Senha trocada, entre com a nova senha.', 5000, 'sucesso');
    navigate('/entrar', { replace: true });
  }

  async function copiarEmail() {
    const texto = emailCompleto.trim();
    if (!texto) return;
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      mostrarToast('E-mail copiado.', 3000, 'sucesso');
    } catch {
      setCopiado(false);
      mostrarToast('Não deu para copiar. Anote o e-mail.', 4000, 'erro');
    }
  }

  const titulo = nome ? `Olá, ${nome}! Crie sua nova senha` : 'Olá! Crie sua nova senha';

  return (
    <FundoEvento>
      <div className="flex min-h-[100dvh] items-center justify-center px-4 py-8">
        <div className="w-full max-w-[440px] overflow-hidden rounded-3xl bg-white/95 shadow-xl backdrop-blur-md">
          <div className="p-6 sm:p-8">
            <div className="mb-5 text-center">
              <span className="text-[20px] font-bold text-primary-600">Se Liga Católico</span>
            </div>

            {estado === 'conferindo' && (
              <div className="animate-pulse">
                <div className="mx-auto h-6 w-48 rounded bg-secondary-100" />
                <div className="mx-auto mt-3 h-4 w-64 rounded bg-secondary-100" />
                <div className="mt-6 h-12 w-full rounded bg-secondary-100" />
                <div className="mt-4 h-12 w-full rounded bg-secondary-100" />
                <div className="mt-6 h-12 w-full rounded-full bg-secondary-100" />
              </div>
            )}

            {estado === 'invalido' && (
              <div className="animate-fade-in text-center">
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-warning-100 text-warning-600">
                  <i className="ri-link-unlink-m text-3xl" aria-hidden />
                </span>
                <h1 className="mt-4 text-lg font-bold text-foreground-950">
                  Este link não vale mais
                </h1>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  {fraseLegivel(erro)}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{TEXTO_FIM}</p>
                <button
                  type="button"
                  onClick={() => navigate('/entrar', { replace: true })}
                  className="mt-6 flex h-12 w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-md bg-primary-500 text-[15px] font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  Ir para o entrar
                </button>
              </div>
            )}

            {estado === 'sucesso' && (
              <div className="animate-fade-in text-center">
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success-100 text-success-600">
                  <i className="ri-checkbox-circle-fill text-3xl" aria-hidden />
                </span>
                <h1 className="mt-4 text-xl font-bold leading-snug text-foreground-950">
                  Senha trocada! ✅
                </h1>

                <p className="mt-4 text-sm text-foreground-700">Para entrar da próxima vez, use:</p>
                <div className="mt-2 rounded-xl border-2 border-primary-200 bg-primary-50 px-4 py-3">
                  <p className="break-all text-[15px] font-bold text-foreground-950">
                    {emailCompleto || 'o e-mail da sua conta'}
                  </p>
                </div>
                {emailCompleto && (
                  <button
                    type="button"
                    onClick={() => void copiarEmail()}
                    className="mt-2 inline-flex h-10 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-white px-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
                  >
                    <i className={copiado ? 'ri-check-line' : 'ri-file-copy-line'} aria-hidden />
                    {copiado ? 'E-mail copiado' : 'Copiar e-mail'}
                  </button>
                )}

                <p className="mt-4 text-sm text-foreground-700">
                  e a senha que você acabou de criar.
                </p>

                <div className="mt-4 flex items-start gap-2 rounded-lg bg-accent-50 px-3 py-2 text-left">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center text-accent-600">
                    <i className="ri-lightbulb-line" aria-hidden />
                  </span>
                  <p className="text-xs leading-relaxed text-foreground-700">
                    Tire um print desta tela para não esquecer.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => void continuar()}
                  disabled={entrando}
                  className="mt-6 flex h-12 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 text-[15px] font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-secondary-200 disabled:text-secondary-400"
                >
                  {entrando ? (
                    <>
                      <span className="flex h-5 w-5 items-center justify-center">
                        <i className="ri-loader-4-line animate-spin" aria-hidden />
                      </span>
                      Entrando…
                    </>
                  ) : (
                    'Continuar'
                  )}
                </button>
              </div>
            )}

            {estado === 'ok' && (
              <div className="animate-fade-in">
                <h1 className="text-center text-xl font-bold leading-snug text-foreground-950">
                  {titulo}
                </h1>
                {emailMascarado && (
                  <p className="mt-2 flex flex-wrap items-center justify-center gap-1 text-center text-sm text-foreground-700">
                    <span>Sua conta:</span>
                    <strong className="font-semibold text-foreground-950">{emailMascarado}</strong>
                  </p>
                )}
                <p className="mt-2 text-center text-sm leading-relaxed text-foreground-600">
                  {pedeCpf || pedeNascimento
                    ? 'Confirme seus dados e escolha a senha nova. Vale por 10 minutos.'
                    : 'Escolha a senha nova. Vale por 10 minutos.'}
                </p>

                <div className="mt-6">
                  <FormularioNovaSenha
                    token={token}
                    pedeCpf={pedeCpf}
                    pedeNascimento={pedeNascimento}
                    aoTrocar={aoTrocar}
                  />
                </div>

                <p className="mt-4 text-center text-xs leading-relaxed text-foreground-500">
                  Depois de criar a senha, você já entra no Se Liga.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </FundoEvento>
  );
}